import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminService } from '../../services/adminService';
import { LoadingState } from '../common/LoadingState';
import { ErrorState } from '../common/ErrorState';

/**
 * Feature Flags Tab
 * Implements Requirement 82 AC 4 / 172 AC7: display and control feature
 * availability per tenant.
 *
 * Deployment-mode badges/chips (Hosted SaaS vs Customer Premises Online vs
 * Offline) were dropped - this platform only runs one way today, so a
 * "parity matrix" comparing modes that don't exist would show fabricated
 * information instead of honestly reflecting nothing to compare.
 */
export const FeatureFlagsTab: React.FC = () => {
  const {
    data: featureFlags,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['featureFlags'],
    queryFn: () => adminService.getFeatureFlags(),
  });

  const handleToggleFlag = async (flagId: string, currentEnabled: boolean) => {
    try {
      await adminService.toggleFeatureFlag(flagId, !currentEnabled);
      refetch();
    } catch (error) {
      alert('Failed to toggle feature flag');
    }
  };

  const enabledCount = featureFlags?.filter((f) => f.enabled).length || 0;
  const totalCount = featureFlags?.length || 0;

  if (isLoading) {
    return <LoadingState message="Loading feature flags..." />;
  }

  if (error) {
    return (
      <ErrorState
        title="Failed to load feature flags"
        message="Unable to fetch feature flag data. Please try again."
        onRetry={refetch}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-sm font-semibold text-foreground">Feature Flags</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Turn platform features on or off for your tenant
        </p>
      </div>

      {/* Statistics */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card">
          <div className="text-sm text-muted-foreground">Total Features</div>
          <div className="mt-1 text-base font-semibold tracking-tight text-foreground">{totalCount}</div>
        </div>
        <div className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card">
          <div className="text-sm text-muted-foreground">Currently Enabled</div>
          <div className="mt-1 text-lg font-bold text-primary">
            {enabledCount}
          </div>
        </div>
      </div>

      {totalCount === 0 && (
        <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground shadow-sm dark:border-border dark:bg-card dark:text-muted-foreground/70">
          No feature flags configured for this tenant yet.
        </div>
      )}

      {/* Feature Flags List */}
      <div className="space-y-3">
        {featureFlags?.map((flag) => (
          <div
            key={flag.id}
            className="rounded-lg border border-border bg-card p-4 shadow-sm dark:border-border dark:bg-card"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h3 className="text-sm font-semibold text-foreground">{flag.name}</h3>
                  {flag.enabled && (
                    <span className="inline-flex rounded-full bg-primary/10 px-2 py-1 text-xs font-semibold text-primary dark:bg-primary/10 dark:text-primary">
                      ENABLED
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{flag.description}</p>
              </div>

              <label className="relative ml-4 inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={flag.enabled}
                  onChange={() => handleToggleFlag(flag.id, flag.enabled)}
                  className="peer sr-only"
                />
                <div className="peer h-6 w-11 rounded-full bg-secondary after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-border after:bg-card after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ring dark:border-border dark:bg-secondary dark:peer-focus:ring-ring"></div>
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
